// MapLocationPicker.jsx
import React, { useState, useCallback, useRef, useEffect } from "react";
import { MapPin, Navigation, X } from "lucide-react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";

/**
 * Props:
 *  - isOpen (bool)
 *  - onClose (fn)
 *  - onLocationSelect (fn) -> receives { lat, lng, address }
 *  - initialLat (number|null)
 *  - initialLng (number|null)
 *  - title (string)
 */

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ["places"];
const containerStyle = { width: "100%", height: "400px" };

const DEFAULT_CENTER = { lat: 61.5240, lng: 105.3188 }; 

export default function MapLocationPicker({
  isOpen,
  onClose,
  onLocationSelect,
  initialLat = null,
  initialLng = null,
  title = "Select Location",
}) {
  const [selectedLocation, setSelectedLocation] = useState(
    initialLat && initialLng
      ? { lat: initialLat, lng: initialLng }
      : DEFAULT_CENTER
  );
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // If parent changes initial coords, update marker
  useEffect(() => {
    if (initialLat != null && initialLng != null) {
      setSelectedLocation({ lat: initialLat, lng: initialLng });
      // attempt reverse geocode (only if map libs loaded)
      if (isLoaded) reverseGeocode(initialLat, initialLng).catch(() => {});
    }
  }, [initialLat, initialLng, isLoaded]);

  // Map load callback
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Autocomplete load
  const onAutocompleteLoad = (auto) => {
    autocompleteRef.current = auto;
  };

  // When user selects a place from autocomplete dropdown
  const onPlaceChanged = async () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    // if place has geometry -> center map and set marker
    if (place && place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setSelectedLocation({ lat, lng });
      setAddress(place.formatted_address || place.name || "");
      if (mapRef.current) mapRef.current.panTo({ lat, lng });
    } else {
      // fallback: try geocoding by place name
      if (place && place.name && isLoaded) {
        try {
          const geocoded = await geocodeAddress(place.name);
          if (geocoded) {
            setSelectedLocation({ lat: geocoded.lat, lng: geocoded.lng });
            setAddress(geocoded.address || "");
            if (mapRef.current)
              mapRef.current.panTo({ lat: geocoded.lat, lng: geocoded.lng });
          }
        } catch (err) {
          // ignore
        }
      }
    }
  };

  // Reverse geocode lat/lng -> human address
  const reverseGeocode = (lat, lng) => {
    return new Promise((resolve) => {
      if (!isLoaded || !window.google) {
        resolve("");
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          setAddress(results[0].formatted_address);
          resolve(results[0].formatted_address);
        } else {
          resolve("");
        }
      });
    });
  };

  // Geocode plain address (fallback)
  const geocodeAddress = (text) => {
    return new Promise((resolve, reject) => {
      if (!isLoaded || !window.google) return resolve(null);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: text }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          resolve({
            lat: loc.lat(),
            lng: loc.lng(),
            address: results[0].formatted_address,
          });
        } else {
          resolve(null);
        }
      });
    });
  };

  // Map click -> set marker and reverse geocode
  const handleMapClick = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelectedLocation({ lat, lng });
    await reverseGeocode(lat, lng);
  };

  // Marker drag end -> update
  const handleMarkerDragEnd = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setSelectedLocation({ lat, lng });
    await reverseGeocode(lat, lng);
  };

  // My Location
  const getCurrentLocation = () => {
    setIsLoading(true);
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      setIsLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setSelectedLocation({ lat, lng });
        if (mapRef.current) mapRef.current.panTo({ lat, lng });
        await reverseGeocode(lat, lng);
        setIsLoading(false);
      },
      (err) => {
        console.error(err);
        alert("Unable to get your location.");
        setIsLoading(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleConfirm = async () => {
    // ensure address is calculated
    // if (!address && isLoaded) {
    //   await reverseGeocode(selectedLocation.lat, selectedLocation.lng);
    // }
    onLocationSelect &&
      onLocationSelect({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        address,
      });
    onClose && onClose();
  };

  if (!isOpen) return null;
  if (loadError) return <div className="p-3">Error loading Google Maps</div>;

  return (
    <div className="modal d-block bg-dark bg-opacity-50" tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <MapPin className="me-2 text-primary" />
              {title}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}/>
              
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* Search + My Location */}
            <div className="d-flex mb-3 gap-2">
              {isLoaded ? (
                <Autocomplete
                  onLoad={onAutocompleteLoad}
                  onPlaceChanged={onPlaceChanged}
                >
                  <input
                    className="form-control"
                    placeholder="Search place or address"
                    style={{ minWidth: 0 }}
                  />
                </Autocomplete>
              ) : (
                <input
                  className="form-control"
                  placeholder="Search (maps loading...)"
                />
              )}

              <button
                className="btn btn-success d-flex align-items-center"
                onClick={getCurrentLocation}
                disabled={isLoading}
              >
                <Navigation className="me-1" size={16} /> My Location
              </button>
            </div>

            {/* Map container */}
            <div className="border rounded mb-3" style={{ overflow: "hidden" }}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={selectedLocation}
                  zoom={14}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                  options={{ fullscreenControl: false }}
                >
                  {/* draggable marker */}
                  <Marker
                    position={selectedLocation}
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                  />
                </GoogleMap>
              ) : (
                <div
                  style={{ height: 400 }}
                  className="d-flex align-items-center justify-content-center"
                >
                  <div>Loading map...</div>
                </div>
              )}
            </div>

            {/* Coordinates inputs */}
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Latitude</label>
                <input
                  type="number"
                  step="any"
                  className="form-control"
                  value={selectedLocation.lat}
                  onChange={(e) =>
                    setSelectedLocation((p) => ({
                      ...p,
                      lat: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Longitude</label>
                <input
                  type="number"
                  step="any"
                  className="form-control"
                  value={selectedLocation.lng}
                  onChange={(e) =>
                    setSelectedLocation((p) => ({
                      ...p,
                      lng: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            {/* Address display */}
            {address && (
              <div className="alert alert-secondary mt-3">
                <strong>Address:</strong> {address}
              </div>
            )}

            {/* Simple loader */}
            {isLoading && (
              <div className="d-flex align-items-center mt-3">
                <div
                  className="spinner-border text-primary me-2"
                  role="status"
                />
                <div>Loading...</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
            >
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
